import React, { useState } from 'react';
import { FaRocket, FaUsers, FaClock, FaDollarSign, FaChartLine, FaChevronDown, FaChevronUp, FaPlay, FaBookmark } from 'react-icons/fa';

const TacticCard = ({ tactic, onImplement, onSave, position = 1, expanded = false, onToggle }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
        if (onToggle) onToggle();
    };

    const getPlatformIcon = (platform) => {
        const platformLower = platform?.toLowerCase() || '';
        if (platformLower.includes('whatsapp')) return '💬';
        if (platformLower.includes('instagram')) return '📷';
        if (platformLower.includes('facebook')) return '📘';
        if (platformLower.includes('email')) return '📧';
        if (platformLower.includes('google')) return '🔍';
        return '📱';
    };

    const getBudgetColor = (budget) => {
        const budgetLower = budget?.toLowerCase() || '';
        if (budgetLower.includes('bajo') || budgetLower.includes('low')) return 'bg-green-100 text-green-800';
        if (budgetLower.includes('medio') || budgetLower.includes('medium')) return 'bg-yellow-100 text-yellow-800';
        if (budgetLower.includes('alto') || budgetLower.includes('high')) return 'bg-red-100 text-red-800';
        return 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                            <FaRocket className="text-green-500 text-xl" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center mb-2">
                                <span className="text-2xl mr-3">{getPlatformIcon(tactic.platform)}</span>
                                <h3 className="text-lg font-bold text-gray-900">
                                    {tactic.title}
                                </h3>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed">
                                {tactic.description}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => onSave(tactic.id)}
                            className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                            title="Guardar táctica"
                        >
                            <FaBookmark />
                        </button>
                        <button
                            onClick={handleToggle}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                    </div>
                </div>

                {/* Key Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center">
                        <FaUsers className="text-blue-500 mr-2" />
                        <div>
                            <div className="text-xs text-gray-500">Audiencia</div>
                            <div className="text-sm font-medium text-gray-900">{tactic.targetAudience}</div>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <FaClock className="text-green-500 mr-2" />
                        <div>
                            <div className="text-xs text-gray-500">Timing</div>
                            <div className="text-sm font-medium text-gray-900">{tactic.timing}</div>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <FaDollarSign className="text-purple-500 mr-2" />
                        <div>
                            <div className="text-xs text-gray-500">Presupuesto</div>
                            <div className="text-sm font-medium text-gray-900">{tactic.budget}</div>
                        </div>
                    </div>
                    <div className="flex items-center">
                        <FaChartLine className="text-orange-500 mr-2" />
                        <div>
                            <div className="text-xs text-gray-500">Plataforma</div>
                            <div className="text-sm font-medium text-gray-900">{tactic.platform}</div>
                        </div>
                    </div>
                </div>

                {/* Action */}
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Acción Específica</h4>
                    <p className="text-sm text-gray-700">{tactic.action}</p>
                </div>

                {/* Message Preview */}
                {tactic.message && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Mensaje/Copy</h4>
                        <p className="text-sm text-gray-700 italic">"{tactic.message}"</p>
                    </div>
                )}
            </div>

            {/* Expandable Content */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-gray-50">
                    <div className="p-6 space-y-6">
                        {/* Success Metrics */}
                        {tactic.successMetrics && tactic.successMetrics.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Métricas de Éxito</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {tactic.successMetrics.map((metric, index) => (
                                        <div key={index} className="flex items-center">
                                            <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                                            <span className="text-sm text-gray-700">{metric}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Implementation Steps */}
                        {tactic.implementationSteps && tactic.implementationSteps.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Pasos de Implementación</h4>
                                <ol className="space-y-2">
                                    {tactic.implementationSteps.map((step, index) => (
                                        <li key={index} className="flex items-start">
                                            <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0 mt-0.5">
                                                {index + 1}
                                            </div>
                                            <span className="text-sm text-gray-700">{step}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}

                        {/* Tools */}
                        {tactic.tools && tactic.tools.length > 0 && (
                            <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Herramientas Necesarias</h4>
                                <div className="flex flex-wrap gap-2">
                                    {tactic.tools.map((tool, index) => (
                                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                            {tool}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="p-6 pt-0">
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={() => onImplement(tactic.id)}
                        className="flex-1 flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <FaPlay className="mr-2" />
                        Implementar Táctica
                    </button>
                    <button
                        onClick={handleToggle}
                        className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                        {isExpanded ? 'Ver Menos' : 'Ver Detalles'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TacticCard; 